import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { 
  Crown, 
  Shield, 
  User,
  MoreVertical,
  Trash2,
  Settings,
  UserCog,
  Clock,
  Pencil,
} from 'lucide-react';
import type { OrganizationMember, MemberPermission } from '@/hooks/useOrganization';

interface MemberCardProps {
  member: OrganizationMember;
  currentUserId: string;
  canManage: boolean;
  permissions: MemberPermission[];
  isPending?: boolean;
  /** Current user's role - used to determine if invited_email should be displayed */
  currentUserRole?: 'owner' | 'admin' | 'member';
  onRemove?: () => void;
  onEditPermissions?: () => void;
  onRoleChange?: (role: 'admin' | 'member') => void;
  onEditName?: (memberId: string, currentName: string) => void;
}

export default function MemberCard({
  member,
  currentUserId,
  canManage,
  permissions,
  isPending,
  currentUserRole,
  onRemove,
  onEditPermissions,
  onRoleChange,
  onEditName,
}: MemberCardProps) {
  const isCurrentUser = member.user_id === currentUserId;
  const isOwner = member.role === 'owner';
  const isAdmin = member.role === 'admin';
  
  // Security: Only owners and admins can see invited_email addresses
  // This provides client-side protection in addition to database-level masking
  const canSeeInvitedEmail = currentUserRole === 'owner' || currentUserRole === 'admin';

  const getRoleIcon = () => {
    if (isOwner) return <Crown className="h-4 w-4 text-yellow-500" />;
    if (isAdmin) return <Shield className="h-4 w-4 text-blue-500" />;
    return <User className="h-4 w-4 text-muted-foreground" />;
  };

  const getRoleBadge = () => {
    if (isPending) {
      return (
        <Badge variant="secondary" className="gap-1">
          <Clock className="h-3 w-3" />
          Pendente
        </Badge>
      );
    }
    if (isOwner) return <Badge className="bg-yellow-500/10 text-yellow-600 hover:bg-yellow-500/20">Proprietário</Badge>;
    if (isAdmin) return <Badge className="bg-blue-500/10 text-blue-600 hover:bg-blue-500/20">Administrador</Badge>;
    return <Badge variant="secondary">Membro</Badge>;
  };

  // Security: Only show invited_email to owners/admins to prevent exposure to regular members
  const safeEmail = canSeeInvitedEmail ? (member.email || member.invited_email) : null;
  const displayName = member.profile?.display_name || safeEmail || 'Usuário';
  const showEmailWithName = Boolean(safeEmail && displayName !== safeEmail);

  const activePermissions = permissions.filter(p => p.can_view || p.can_create || p.can_edit || p.can_delete);

  return (
    <div className="flex items-center justify-between p-4 rounded-lg border bg-card">
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-full bg-muted flex items-center justify-center">
          {getRoleIcon()}
        </div>
        <div>
          <div className="flex items-center gap-2">
            <p className="font-medium">
              {displayName}{showEmailWithName ? ` - ${safeEmail}` : ''}
            </p>
            {isCurrentUser && (
              <Badge variant="outline" className="text-xs">Você</Badge>
            )}
          </div>
          {!isPending && !isOwner && !isAdmin && activePermissions.length > 0 && (
            <p className="text-xs text-muted-foreground mt-1">
              {activePermissions.length} módulo(s) com acesso
            </p>
          )}
        </div>
      </div>

      <div className="flex items-center gap-2">
        {getRoleBadge()}
        
        {canManage && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon">
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {!isPending && (
                <>
                  <DropdownMenuItem onClick={() => onEditName?.(member.id, displayName)}>
                    <Pencil className="h-4 w-4 mr-2" />
                    Editar Nome
                  </DropdownMenuItem>
                  {!isOwner && !isCurrentUser && (
                    <>
                      <DropdownMenuItem onClick={onEditPermissions}>
                        <Settings className="h-4 w-4 mr-2" />
                        Editar Permissões
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      {isAdmin ? (
                        <DropdownMenuItem onClick={() => onRoleChange?.('member')}>
                          <UserCog className="h-4 w-4 mr-2" />
                          Tornar Membro
                        </DropdownMenuItem>
                      ) : (
                        <DropdownMenuItem onClick={() => onRoleChange?.('admin')}>
                          <Shield className="h-4 w-4 mr-2" />
                          Tornar Administrador
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuSeparator />
                    </>
                  )}
                </>
              )}
              {!isOwner && !isCurrentUser && (
                <DropdownMenuItem 
                  onClick={onRemove}
                  className="text-destructive focus:text-destructive"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  {isPending ? 'Cancelar Convite' : 'Remover'}
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
    </div>
  );
}
